import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../../App';
import { Building2, Mail, RefreshCw, XCircle, Clock, CheckCircle, AlertTriangle, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const STATUS_STYLES = {
  pending: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  revoked: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-600',
};

const STATUS_ICONS = {
  pending: Clock,
  accepted: CheckCircle,
  revoked: XCircle,
  expired: AlertTriangle,
};

const PartnerInvites = () => {
  const { user, logout } = useContext(AuthContext);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'partner_staff' });
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchInvites(); }, []);

  const fetchInvites = async () => {
    try {
      const res = await axios.get(`${API}/partner-invites`);
      setInvites(res.data);
    } catch (err) {
      toast.error('Failed to load invites');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (invite) => {
    if (!window.confirm(`Cancel invite for ${invite.email}?\n\nThis will invalidate the invite link and prevent them from joining.`)) return;
    try {
      await axios.post(`${API}/partner-invites/${invite.id}/revoke`);
      toast.success(`Invite for ${invite.email} cancelled`);
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel invite');
    }
  };

  const handleResend = async (invite) => {
    try {
      await axios.post(`${API}/partner-invites/${invite.id}/resend`);
      toast.success(`Invite resent to ${invite.email}`);
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/partner-invites`, {
        email: inviteForm.email,
        org_id: user?.partner_org_id,
        role: inviteForm.role,
      });
      toast.success(`Invite sent to ${inviteForm.email}`);
      setInviteForm({ email: '', role: 'partner_staff' });
      setShowInviteForm(false);
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  const filtered = filter === 'all' ? invites : invites.filter(i => i.status === filter);

  const formatDate = (dateStr) => {
    try { return format(new Date(dateStr), 'MMM d, yyyy h:mm a'); } catch { return dateStr; }
  };

  const isExpired = (invite) => {
    return invite.status === 'pending' && new Date(invite.expires_at) < new Date();
  };

  const isAdmin = user?.role === 'partner_admin';

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-lg">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-[#FBF4EA]" data-testid="partner-invites-page">
      <nav className="bg-white border-b-2 border-[#E9DDCF]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#8B2E3C]" />
              <div className="font-bold text-lg text-[#222]">{user?.partner_org_name || 'Partner Portal'}</div>
            </div>
            <div className="flex items-center gap-4 text-sm overflow-x-auto">
              <Link to="/partner/dashboard" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Dashboard</Link>
              <Link to="/partner/opportunities" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Opportunities</Link>
              <Link to="/partner/community" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Community Board</Link>
              <Link to="/partner/participants" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Participants</Link>
              <Link to="/partner/invites" className="text-[#8B2E3C] font-bold whitespace-nowrap">Invites</Link>
              <Link to="/partner/staff" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Staff</Link>
              <Link to="/partner/time-entries" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Time Entries</Link>
              <Link to="/partner/reports" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Reports</Link>
              <button onClick={logout} className="text-[#8B2E3C] hover:underline whitespace-nowrap">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#222]">Invites</h1>
            <p className="text-[#666] mt-1">Manage invitations for your organization</p>
          </div>
          <button onClick={() => setShowInviteForm(!showInviteForm)} className={`btn-primary flex items-center gap-2 ${!isAdmin ? 'hidden' : ''}`} data-testid="new-invite-btn">
            <UserPlus className="w-4 h-4" /> New Invite
          </button>
        </div>

        {/* New Invite Form */}
        {showInviteForm && (
          <div className="card mb-6" data-testid="invite-form">
            <h3 className="font-bold text-[#222] mb-4">Send New Invite</h3>
            <form onSubmit={handleSendInvite} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Email address"
                className="input-field flex-1"
                required
                data-testid="invite-email-input"
              />
              <select
                value={inviteForm.role}
                onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
                className="input-field sm:w-40"
                data-testid="invite-role-select"
              >
                <option value="partner_staff">Staff</option>
                <option value="partner_admin">Admin</option>
              </select>
              <div className="flex gap-2">
                <button type="submit" disabled={sending} className="btn-primary whitespace-nowrap" data-testid="send-invite-btn">
                  {sending ? 'Sending...' : 'Send Invite'}
                </button>
                <button type="button" onClick={() => setShowInviteForm(false)} className="btn-outline">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['all', 'pending', 'accepted', 'revoked'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-sm rounded-lg ${filter === f ? 'bg-[#8B2E3C] text-white' : 'bg-white text-[#666] border border-[#E9DDCF] hover:bg-[#FBF4EA]'}`}
              data-testid={`filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)} {f !== 'all' && `(${invites.filter(i => i.status === f).length})`}
            </button>
          ))}
        </div>

        {/* Invites List */}
        {filtered.length === 0 ? (
          <div className="card text-center py-12 text-[#666]">
            {invites.length === 0 ? 'No invites sent yet. Click "New Invite" to get started.' : `No ${filter} invites.`}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(invite => {
              const expired = isExpired(invite);
              const StatusIcon = STATUS_ICONS[expired ? 'expired' : invite.status] || Clock;
              const displayStatus = expired ? 'expired' : invite.status;

              return (
                <div key={invite.id} className="card" data-testid={`invite-row-${invite.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-[#E9DDCF] flex items-center justify-center shrink-0">
                        <Mail className="w-5 h-5 text-[#8B2E3C]" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-[#222] truncate">{invite.email}</div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-[#666] mt-0.5">
                          <span className="bg-[#FBF4EA] px-2 py-0.5 rounded">{invite.role === 'partner_admin' ? 'Admin' : 'Staff'}</span>
                          <span>Sent {formatDate(invite.created_at)}</span>
                          {invite.send_count > 1 && <span className="text-[#C9A227]">Resent {invite.send_count - 1}x</span>}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[displayStatus] || STATUS_STYLES.pending}`}>
                        <StatusIcon className="w-3 h-3" />
                        {displayStatus}
                      </span>

                      {isAdmin && invite.status === 'pending' && !expired && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleResend(invite)}
                            className="p-1.5 text-[#C9A227] hover:bg-yellow-50 rounded transition-colors"
                            title="Resend invite"
                            data-testid={`resend-invite-${invite.id}`}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRevoke(invite)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Cancel invite"
                            data-testid={`cancel-invite-${invite.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expiry info for pending */}
                  {invite.status === 'pending' && (
                    <div className="mt-2 text-xs text-[#999]">
                      {expired
                        ? 'This invite has expired.'
                        : `Expires ${formatDate(invite.expires_at)}`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerInvites;
